/**
 * Camera survey — forked from vCIP_droom_4.0/src/media/switchFacing.ts (the enumeration half:
 * `surveyCameras` / `LABEL_HINT` / capability matching). The LiveKit `restartTrack` ladder is NOT
 * carried over — svapp owns its own getUserMedia stream, so a facing switch is a fresh acquisition
 * in useCaptureCamera.ts. What is kept is the honest part: how to NAME the target lens so the
 * browser cannot reinterpret a bare `facingMode` hint (the owner's-handset finding, 2026-08-10).
 *
 * Enumeration only — never getUserMedia here (a second acquisition steals the camera on iOS).
 */

export type CameraFacing = 'user' | 'environment';

export type DeviceEnumerator = () => Promise<MediaDeviceInfo[]>;

const OPPOSITE: Record<CameraFacing, CameraFacing> = { user: 'environment', environment: 'user' };

/**
 * Label shapes real browsers produce, post-permission (labels are empty until the camera has been
 * granted once). Conservative: a label matching BOTH families, or neither, resolves to no device.
 */
const LABEL_HINT: Record<CameraFacing, RegExp> = {
  environment: /\b(back|rear|environment)\b/i,
  user: /\b(front|user|selfie)\b/i,
};

function capabilityFacings(device: MediaDeviceInfo): readonly string[] {
  try {
    const caps = (device as Partial<InputDeviceInfo>).getCapabilities?.();
    const facing = (caps as MediaTrackCapabilities | undefined)?.facingMode;
    return Array.isArray(facing) ? facing : [];
  } catch {
    return [];
  }
}

export function browserEnumerator(): Promise<MediaDeviceInfo[]> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return Promise.resolve([]);
  }
  return navigator.mediaDevices.enumerateDevices();
}

export interface CameraSurvey {
  /** Zero means "enumeration told us nothing", never "no cameras". */
  readonly cameraCount: number;
  /** The deviceId that PROVABLY faces `facing`, or null. */
  readonly targetDeviceId: string | null;
}

export async function surveyCameras(
  facing: CameraFacing,
  enumerate: DeviceEnumerator = browserEnumerator,
): Promise<CameraSurvey> {
  let devices: MediaDeviceInfo[];
  try {
    devices = await enumerate();
  } catch {
    return { cameraCount: 0, targetDeviceId: null };
  }
  const cameras = devices.filter(
    (d) => d.kind === 'videoinput' && typeof d.deviceId === 'string' && d.deviceId !== '',
  );
  const cameraCount = cameras.length;
  if (cameraCount < 2) return { cameraCount, targetDeviceId: null };

  const byCapability = cameras.filter((d) => capabilityFacings(d).includes(facing));
  if (byCapability.length > 0) return { cameraCount, targetDeviceId: byCapability[0]!.deviceId };

  const byLabel = cameras.filter(
    (d) =>
      typeof d.label === 'string' &&
      LABEL_HINT[facing].test(d.label) &&
      !LABEL_HINT[OPPOSITE[facing]].test(d.label),
  );
  if (byLabel.length > 0) return { cameraCount, targetDeviceId: byLabel[0]!.deviceId };
  return { cameraCount, targetDeviceId: null };
}
